import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/CookieConsent.css';
import { usePublicAppContext } from '../contexts/PublicAppContext';
import {
    COOKIE_SETTINGS_EVENT,
    COOKIE_PREFERENCES_UPDATED_EVENT,
    DEFAULT_COOKIE_PREFERENCES,
    isGlobalPrivacyControlEnabled,
    readCookieConsentState,
    writeCookieConsentState
} from '../services/cookiePreferences';
import { localizeInternalHref } from '../utils/localeRouting';
import { COOKIE_CONSENT_COPY_BN } from '../data/i18n/surfaceChrome.bn.js';
import { COOKIE_CONSENT_COPY_HI } from '../data/i18n/surfaceChrome.hi.js';
import { COOKIE_CONSENT_COPY_PT } from '../data/i18n/surfaceChrome.pt.js';

const TRANSLATIONS = {
    en: {
        text: "We use cookies and similar technologies for required functionality, analytics, and ad measurement (Google and Meta).",
        detail: "If you reject, optional analytics and ad measurement remain off. Learn more in our Privacy Notice.",
        manage: "Cookie Settings",
        save: "Save Preferences",
        essentialTitle: "Essential Cookies",
        essentialDesc: "Required for security, login, and core site functionality.",
        analyticsTitle: "Analytics Cookies",
        analyticsDesc: "Help us understand traffic and product usage (for example Google Analytics).",
        advertisingTitle: "Advertising Measurement",
        advertisingDesc: "Measure ad performance and conversions (for example Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control is enabled in your browser, so advertising measurement stays off.",
        saveError: "We couldn't save your cookie preferences in this browser. Check your storage or privacy settings and try again.",
        accept: "Accept All",
        reject: "Reject Optional",
        learnMore: "Privacy Notice"
    },
    tr: {
        text: "Zorunlu işlevler, analiz ve reklam ölçümü (Google ve Meta) için çerezler ve benzer teknolojiler kullanıyoruz.",
        detail: "Reddettiğinizde isteğe bağlı analitik ve reklam ölçümü kapalı kalır. Ayrıntılar için Gizlilik ve KVKK Aydınlatma Metni’ni inceleyin.",
        manage: "Çerez Ayarları",
        save: "Tercihleri Kaydet",
        essentialTitle: "Zorunlu Çerezler",
        essentialDesc: "Güvenlik, giriş ve temel site işlevleri için gereklidir.",
        analyticsTitle: "Analitik Çerezler",
        analyticsDesc: "Trafik ve ürün kullanımını anlamamıza yardımcı olur (ör. Google Analytics).",
        advertisingTitle: "Reklam Ölçümü",
        advertisingDesc: "Reklam performansı ve dönüşümleri ölçer (ör. Meta Pixel/CAPI).",
        gpcNotice: "Tarayıcınızda Global Privacy Control açık olduğu için reklam ölçümü kapalı kalır.",
        saveError: "Çerez tercihleriniz bu tarayıcıya kaydedilemedi. Depolama veya gizlilik ayarlarınızı kontrol edip tekrar deneyin.",
        accept: "Tümünü Kabul Et",
        reject: "Opsiyonelleri Reddet",
        learnMore: "Gizlilik ve KVKK Metni"
    },
    de: {
        text: "Wir verwenden Cookies und ähnliche Technologien für notwendige Funktionen, Analysen und Werbemessung (Google und Meta).",
        detail: "Wenn Sie ablehnen, bleiben optionale Analysen und Werbemessungen deaktiviert. Details finden Sie in unserem Datenschutzhinweis.",
        manage: "Cookie-Einstellungen",
        save: "Einstellungen speichern",
        essentialTitle: "Notwendige Cookies",
        essentialDesc: "Erforderlich für Sicherheit, Login und Kernfunktionen.",
        analyticsTitle: "Analyse-Cookies",
        analyticsDesc: "Helfen uns, Traffic und Nutzung zu verstehen (z. B. Google Analytics).",
        advertisingTitle: "Werbemessung",
        advertisingDesc: "Misst Anzeigenleistung und Conversions (z. B. Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control ist in Ihrem Browser aktiviert, daher bleibt die Werbemessung deaktiviert.",
        saveError: "Ihre Cookie-Einstellungen konnten in diesem Browser nicht gespeichert werden. Prüfen Sie die Speicher- oder Datenschutzeinstellungen und versuchen Sie es erneut.",
        accept: "Alle akzeptieren",
        reject: "Optionale ablehnen",
        learnMore: "Datenschutz"
    },
    es: {
        text: "Usamos cookies y tecnologías similares para funciones necesarias, analíticas y medición publicitaria (Google y Meta).",
        detail: "Si rechaza, las analíticas y la medición publicitaria opcionales seguirán desactivadas. Consulte nuestro Aviso de privacidad.",
        manage: "Ajustes de cookies",
        save: "Guardar preferencias",
        essentialTitle: "Cookies esenciales",
        essentialDesc: "Necesarias para seguridad, inicio de sesión y funciones básicas.",
        analyticsTitle: "Cookies analíticas",
        analyticsDesc: "Nos ayudan a entender tráfico y uso del producto (p. ej., Google Analytics).",
        advertisingTitle: "Medición publicitaria",
        advertisingDesc: "Mide rendimiento de anuncios y conversiones (p. ej., Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control está activado en su navegador, por lo que la medición publicitaria permanece desactivada.",
        saveError: "No pudimos guardar sus preferencias de cookies en este navegador. Revise la configuración de almacenamiento o privacidad e inténtelo de nuevo.",
        accept: "Aceptar todo",
        reject: "Rechazar opcionales",
        learnMore: "Política de Privacidad"
    },
    fr: {
        text: "Nous utilisons des cookies et technologies similaires pour les fonctions nécessaires, l’analyse et la mesure publicitaire (Google et Meta).",
        detail: "Si vous refusez, l’analyse et la mesure publicitaire facultatives restent désactivées. Consultez notre Avis de confidentialité.",
        manage: "Paramètres des cookies",
        save: "Enregistrer les préférences",
        essentialTitle: "Cookies essentiels",
        essentialDesc: "Nécessaires pour la sécurité, la connexion et les fonctions de base.",
        analyticsTitle: "Cookies analytiques",
        analyticsDesc: "Aident à comprendre le trafic et l’usage produit (ex. Google Analytics).",
        advertisingTitle: "Mesure publicitaire",
        advertisingDesc: "Mesure la performance des annonces et les conversions (ex. Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control est activé dans votre navigateur ; la mesure publicitaire reste donc désactivée.",
        saveError: "Nous n’avons pas pu enregistrer vos préférences de cookies dans ce navigateur. Vérifiez les paramètres de stockage ou de confidentialité, puis réessayez.",
        accept: "Tout accepter",
        reject: "Refuser l’optionnel",
        learnMore: "Politique de confidentialité"
    },
    it: {
        text: "Utilizziamo cookie e tecnologie simili per le funzioni necessarie, l’analisi e la misurazione pubblicitaria (Google e Meta).",
        detail: "Se rifiuti, analisi e misurazione pubblicitaria opzionali restano disattivate. Consulta la nostra Informativa sulla privacy.",
        manage: "Impostazioni cookie",
        save: "Salva preferenze",
        essentialTitle: "Cookie essenziali",
        essentialDesc: "Necessari per sicurezza, accesso e funzioni principali del sito.",
        analyticsTitle: "Cookie analitici",
        analyticsDesc: "Ci aiutano a capire traffico e utilizzo del prodotto (ad esempio Google Analytics).",
        advertisingTitle: "Misurazione pubblicitaria",
        advertisingDesc: "Misura il rendimento degli annunci e le conversioni (ad esempio Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control è attivo nel browser, quindi la misurazione pubblicitaria rimane disattivata.",
        saveError: "Non è stato possibile salvare le preferenze sui cookie in questo browser. Controlla le impostazioni di archiviazione o privacy e riprova.",
        accept: "Accetta tutto",
        reject: "Rifiuta gli opzionali",
        learnMore: "Informativa sulla privacy"
    },
    id: {
        text: "Kami menggunakan cookie dan teknologi serupa untuk fungsi wajib, analitik, dan pengukuran iklan (Google dan Meta).",
        detail: "Jika Anda menolak, analitik dan pengukuran iklan opsional tetap nonaktif. Pelajari lebih lanjut dalam Pemberitahuan Privasi kami.",
        manage: "Pengaturan Cookie",
        save: "Simpan Preferensi",
        essentialTitle: "Cookie Esensial",
        essentialDesc: "Diperlukan untuk keamanan, login, dan fungsi inti situs.",
        analyticsTitle: "Cookie Analitik",
        analyticsDesc: "Membantu kami memahami trafik dan penggunaan produk (misalnya Google Analytics).",
        advertisingTitle: "Pengukuran Iklan",
        advertisingDesc: "Mengukur performa iklan dan konversi (misalnya Meta Pixel/CAPI).",
        gpcNotice: "Global Privacy Control aktif di browser Anda, sehingga pengukuran iklan tetap dinonaktifkan.",
        saveError: "Preferensi cookie tidak dapat disimpan di browser ini. Periksa pengaturan penyimpanan atau privasi, lalu coba lagi.",
        accept: "Terima Semua",
        reject: "Tolak Opsional",
        learnMore: "Kebijakan Privasi"
    },
    th: {
        text: "เราใช้คุกกี้และเทคโนโลยีที่คล้ายกันสำหรับฟังก์ชันที่จำเป็น การวิเคราะห์ และการวัดผลโฆษณา (Google และ Meta)",
        detail: "หากคุณปฏิเสธ การวิเคราะห์และการวัดผลโฆษณาที่เป็นตัวเลือกจะยังคงปิดอยู่ โปรดดูประกาศความเป็นส่วนตัวของเรา",
        manage: "การตั้งค่าคุกกี้",
        save: "บันทึกการตั้งค่า",
        essentialTitle: "คุกกี้ที่จำเป็น",
        essentialDesc: "จำเป็นต่อความปลอดภัย การเข้าสู่ระบบ และการทำงานหลักของเว็บไซต์",
        analyticsTitle: "คุกกี้วิเคราะห์",
        analyticsDesc: "ช่วยให้เราเข้าใจทราฟฟิกและการใช้งานผลิตภัณฑ์ (เช่น Google Analytics)",
        advertisingTitle: "การวัดผลโฆษณา",
        advertisingDesc: "ใช้วัดประสิทธิภาพโฆษณาและคอนเวอร์ชัน (เช่น Meta Pixel/CAPI)",
        gpcNotice: "เบราว์เซอร์ของคุณเปิดใช้ Global Privacy Control อยู่ การวัดผลโฆษณาจึงยังคงปิดไว้",
        saveError: "ไม่สามารถบันทึกการตั้งค่าคุกกี้ในเบราว์เซอร์นี้ได้ โปรดตรวจสอบการตั้งค่าพื้นที่จัดเก็บหรือความเป็นส่วนตัวแล้วลองอีกครั้ง",
        accept: "ยอมรับทั้งหมด",
        reject: "ปฏิเสธตัวเลือกเสริม",
        learnMore: "นโยบายความเป็นส่วนตัว"
    }
};
Object.assign(TRANSLATIONS, {
    bn: COOKIE_CONSENT_COPY_BN,
    hi: COOKIE_CONSENT_COPY_HI,
    pt: COOKIE_CONSENT_COPY_PT
});

const PREFERRED_DEFAULT_SELECTION = Object.freeze({
    essential: true,
    analytics: false,
    advertising: false
});

const CookieConsent = ({ onConsentChange }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState({ ...DEFAULT_COOKIE_PREFERENCES });
    const [persistenceError, setPersistenceError] = useState(false);
    const dialogRef = useRef(null);
    const manageButtonRef = useRef(null);
    const returnFocusRef = useRef(null);
    const wasVisibleRef = useRef(false);
    const showPreferencesRef = useRef(showPreferences);
    const localWriteInProgressRef = useRef(false);
    const { language } = usePublicAppContext();
    const t = TRANSLATIONS[language] || TRANSLATIONS['en'];
    const isGpcEnabled = isGlobalPrivacyControlEnabled();
    showPreferencesRef.current = showPreferences;

    const notifyParent = useCallback((nextPreferences) => {
        onConsentChange?.({
            essential: true,
            analytics: Boolean(nextPreferences?.analytics),
            advertising: Boolean(nextPreferences?.advertising)
        });
    }, [onConsentChange]);

    const syncFromStorage = useCallback(({ forceVisible = false, openPreferences = false } = {}) => {
        const state = readCookieConsentState();
        const storedPreferences = state.preferences || { ...DEFAULT_COOKIE_PREFERENCES };
        const initialPreferences = state.status === 'unknown'
            ? { ...PREFERRED_DEFAULT_SELECTION }
            : storedPreferences;
        setPreferences(initialPreferences);
        setPersistenceError(false);

        // Keep tracking disabled until the user explicitly saves/accepts.
        const effectivePreferences = state.status === 'unknown'
            ? { ...DEFAULT_COOKIE_PREFERENCES }
            : storedPreferences;
        notifyParent(effectivePreferences);

        if (forceVisible) {
            setIsVisible(true);
        } else {
            const shouldShowBanner = state.status === 'unknown' || !state.hasCurrentVersion;
            setIsVisible(shouldShowBanner);
        }

        if (openPreferences) {
            setShowPreferences(true);
        } else if (state.status === 'unknown') {
            setShowPreferences(false);
        }
    }, [notifyParent]);

    const applyPreferences = useCallback((nextPreferences, source = 'cookie-banner') => {
        const requestedPreferences = {
            essential: true,
            analytics: Boolean(nextPreferences?.analytics),
            advertising: Boolean(nextPreferences?.advertising)
        };

        localWriteInProgressRef.current = true;
        let result = null;
        try {
            result = writeCookieConsentState(requestedPreferences, { source });
        } catch {
            // Treat unexpected runtime failures like a persistence failure and keep the dialog open.
        } finally {
            localWriteInProgressRef.current = false;
        }

        if (!result) {
            setPersistenceError(true);
            setIsVisible(true);
            return;
        }

        const normalizedPreferences = result.preferences;
        setPreferences(normalizedPreferences);

        if (!result.persisted) {
            setPersistenceError(true);
            setIsVisible(true);
            return;
        }

        setPersistenceError(false);
        notifyParent(normalizedPreferences);
        setIsVisible(false);
        setShowPreferences(false);
    }, [notifyParent]);

    useEffect(() => {
        syncFromStorage();

        const handleOpenSettings = () => {
            syncFromStorage({ forceVisible: true, openPreferences: true });
        };

        const handlePreferencesUpdated = (event) => {
            const nextPreferences = event?.detail?.preferences || readCookieConsentState().preferences;
            const normalizedPreferences = {
                essential: true,
                analytics: Boolean(nextPreferences?.analytics),
                advertising: Boolean(nextPreferences?.advertising)
            };
            setPreferences(normalizedPreferences);
            setPersistenceError(false);
            if (!localWriteInProgressRef.current) {
                notifyParent(normalizedPreferences);
            }
        };

        window.addEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);
        window.addEventListener(COOKIE_PREFERENCES_UPDATED_EVENT, handlePreferencesUpdated);

        window.openCookieSettings = () => {
            window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
        };

        return () => {
            window.removeEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);
            window.removeEventListener(COOKIE_PREFERENCES_UPDATED_EVENT, handlePreferencesUpdated);
            if (window.openCookieSettings) {
                delete window.openCookieSettings;
            }
        };
    }, [notifyParent, syncFromStorage]);

    useEffect(() => {
        if (isVisible && !wasVisibleRef.current) {
            returnFocusRef.current = document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
            wasVisibleRef.current = true;
            return undefined;
        }

        if (!isVisible && wasVisibleRef.current) {
            const returnTarget = returnFocusRef.current;
            wasVisibleRef.current = false;
            returnFocusRef.current = null;
            window.requestAnimationFrame(() => {
                if (returnTarget?.isConnected) returnTarget.focus();
            });
        }

        return undefined;
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return undefined;

        const dialogNode = dialogRef.current;
        if (!dialogNode) return undefined;

        const getFocusableElements = () => Array.from(
            dialogNode.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        );

        const focusableElements = getFocusableElements();
        (manageButtonRef.current || focusableElements[0])?.focus();

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && showPreferencesRef.current) {
                event.preventDefault();
                setShowPreferences(false);
                window.requestAnimationFrame(() => manageButtonRef.current?.focus());
                return;
            }

            if (event.key !== 'Tab') return;
            const currentFocusable = getFocusableElements();
            if (!currentFocusable.length) return;

            const first = currentFocusable[0];
            const last = currentFocusable[currentFocusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        dialogNode.addEventListener('keydown', handleKeyDown);
        return () => dialogNode.removeEventListener('keydown', handleKeyDown);
    }, [isVisible]);

    const handleAccept = () => {
        applyPreferences({ analytics: true, advertising: true }, 'accept-all');
    };

    const handleReject = () => {
        applyPreferences({ analytics: false, advertising: false }, 'reject-optional');
    };

    const handleSavePreferences = () => {
        applyPreferences(preferences, 'save-preferences');
    };

    const handleTogglePreference = (key) => (event) => {
        const checked = Boolean(event?.target?.checked);
        setPreferences((prev) => ({
            ...prev,
            [key]: checked
        }));
    };

    if (!isVisible) return null;

    const essentialTitleId = 'cookie-pref-essential-title';
    const essentialDescId = 'cookie-pref-essential-desc';
    const analyticsTitleId = 'cookie-pref-analytics-title';
    const analyticsDescId = 'cookie-pref-analytics-desc';
    const advertisingTitleId = 'cookie-pref-advertising-title';
    const advertisingDescId = 'cookie-pref-advertising-desc';
    const advertisingGpcId = 'cookie-pref-advertising-gpc';
    const essentialInputId = 'cookie-pref-essential';
    const analyticsInputId = 'cookie-pref-analytics';
    const advertisingInputId = 'cookie-pref-advertising';

    return (
        <div
            ref={dialogRef}
            className="cookie-consent-banner"
            role="dialog"
            aria-modal="true"
            aria-live="polite"
            aria-labelledby="cookie-consent-title"
            aria-describedby="cookie-consent-description"
        >
            <div className="cookie-consent-content">
                <h2 id="cookie-consent-title" className="sr-only">{t.manage}</h2>
                <p>{t.text}</p>
                <p id="cookie-consent-description" className="cookie-consent-detail">
                    {t.detail}{' '}
                    <a className="cookie-consent-link" href={localizeInternalHref('/privacy', language)}>
                        {t.learnMore}
                    </a>
                </p>

                {showPreferences && (
                    <div className="cookie-preferences-panel">
                        <label className="cookie-pref-row cookie-pref-required" htmlFor={essentialInputId}>
                            <span className="cookie-pref-copy">
                                <span id={essentialTitleId} className="cookie-pref-title">{t.essentialTitle}</span>
                                <span id={essentialDescId} className="cookie-pref-desc">{t.essentialDesc}</span>
                            </span>
                            <input
                                id={essentialInputId}
                                name="cookies-essential"
                                autoComplete="off"
                                type="checkbox"
                                checked
                                disabled
                                aria-labelledby={essentialTitleId}
                                aria-describedby={essentialDescId}
                            />
                        </label>
                        <label className="cookie-pref-row" htmlFor={analyticsInputId}>
                            <span className="cookie-pref-copy">
                                <span id={analyticsTitleId} className="cookie-pref-title">{t.analyticsTitle}</span>
                                <span id={analyticsDescId} className="cookie-pref-desc">{t.analyticsDesc}</span>
                            </span>
                            <input
                                id={analyticsInputId}
                                name="cookies-analytics"
                                autoComplete="off"
                                type="checkbox"
                                checked={Boolean(preferences.analytics)}
                                onChange={handleTogglePreference('analytics')}
                                aria-labelledby={analyticsTitleId}
                                aria-describedby={analyticsDescId}
                            />
                        </label>
                        <label className="cookie-pref-row" htmlFor={advertisingInputId}>
                            <span className="cookie-pref-copy">
                                <span id={advertisingTitleId} className="cookie-pref-title">{t.advertisingTitle}</span>
                                <span id={advertisingDescId} className="cookie-pref-desc">{t.advertisingDesc}</span>
                                {isGpcEnabled && (
                                    <span id={advertisingGpcId} className="cookie-pref-desc" role="note">
                                        {t.gpcNotice}
                                    </span>
                                )}
                            </span>
                            <input
                                id={advertisingInputId}
                                name="cookies-advertising"
                                autoComplete="off"
                                type="checkbox"
                                checked={isGpcEnabled ? false : Boolean(preferences.advertising)}
                                disabled={isGpcEnabled}
                                onChange={handleTogglePreference('advertising')}
                                aria-labelledby={advertisingTitleId}
                                aria-describedby={`${advertisingDescId}${isGpcEnabled ? ` ${advertisingGpcId}` : ''}`}
                            />
                        </label>
                    </div>
                )}

                {persistenceError && (
                    <p className="cookie-consent-detail" role="alert" aria-live="assertive">
                        {t.saveError}
                    </p>
                )}

                <div className="cookie-consent-actions">
                    <button
                        ref={manageButtonRef}
                        type="button"
                        onClick={showPreferences ? handleSavePreferences : () => setShowPreferences(true)}
                        className="cookie-btn manage"
                    >
                        {showPreferences ? t.save : t.manage}
                    </button>
                    <button type="button" onClick={handleReject} className="cookie-btn reject">
                        {t.reject}
                    </button>
                    <button type="button" onClick={handleAccept} className="cookie-btn accept">
                        {t.accept}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;
